import { z } from 'zod'
import { orderCoreSchema } from '../../OrderForm/orderCore.schema'
import type { CompanyItemType } from './types'

export interface NamedOption { id: number; name: string }

/** Aceita `NaN`: os ids vêm de `Number(...)` sobre campos opcionais e o builder do payload já
 *  trata `NaN` como "sem id". `z.number()` reprovaria saves que hoje funcionam. */
const looseNumber = z.custom<number>((val) => typeof val === 'number')

const displayValue = z.custom<string>((val) => typeof val === 'string' || typeof val === 'number')

/** Checa só que é objeto: descrever os campos recusaria o save de um registro com campo nulo. */
const isObject = (val: unknown) => typeof val === 'object' && val !== null

const companySelectionSchema = z.custom<CompanyItemType>(isObject)

const namedOptionSchema = z.custom<NamedOption>(isObject)

/** Contrato vivo: o steel-orders converte em rows de `OrderItemGrade` e o sales-stock gera uma peça por unidade. */
export const fashionGradeItemSchema = z.object({
  colorId: looseNumber,
  size: z.string(),
  quantity: looseNumber,
})

export const fashionOrderItemSchema = z.object({
  fashionProductId: looseNumber.optional(),
  discount: looseNumber.nullable(),
  sellingPrice: looseNumber.nullable().optional(),
  totalItems: looseNumber,
  subtotal: looseNumber,
  items: z.array(fashionGradeItemSchema),
  fashionOrderId: looseNumber.optional(),
  fashionProduct: z
    .object({
      id: looseNumber,
      name: z.string(),
      price: looseNumber,
      size: z.array(z.string()),
      group: z.object({
        id: looseNumber,
        name: z.string().optional(),
        family: z
          .object({
            id: looseNumber,
            name: z.string().optional(),
            collectionId: looseNumber.optional(),
          })
          .optional(),
      }),
    })
    .optional(),
  colorCount: looseNumber.optional(),
  colors: z.array(z.string()).optional(),
  family: z
    .object({ id: looseNumber, name: z.string().optional(), familyId: looseNumber.optional() })
    .optional(),
  group: z.object({ id: looseNumber, name: z.string().optional() }).optional(),
  name: z.string().optional(),
  price: looseNumber.optional(),
  selectedColors: z.array(looseNumber).optional(),
  discountValue: looseNumber.optional(),
  sizeQuantities: z.record(z.string(), looseNumber).optional(),
  kitIndex: looseNumber.nullable().optional(),
  isKitIntent: z.boolean().optional(),
  nomeCustomizado: z.string().optional(),
})

export const installmentSchema = z.object({
  id: looseNumber.optional(),
  numero: looseNumber,
  valor: looseNumber,
  vencimento: z.string(),
  diasOffset: looseNumber.optional(),
  comissaoAtiva: z.boolean().optional(),
})

export const fashionOrderSchema = orderCoreSchema.extend({
  carrier: companySelectionSchema,
  quantityItems: displayValue,
  quantityPieces: displayValue,
  totalCost: displayValue,
  deliveryTime: z.string(),
  shippingCost: z.string(),
  maximumBillingAmount: z.string(),
  installments: z.array(installmentSchema),
})

export type FashionOrderFormValues = z.infer<typeof fashionOrderSchema>
export type FashionGradeItem = z.infer<typeof fashionGradeItemSchema>
export type FashionOrderInstallment = z.infer<typeof installmentSchema>
